'use client';

import { Link } from 'src/navigation';
import cn from 'classnames';
import type { SanityOperatingSchedule } from '@metmuseum/types';
import { useLocale, useTranslations } from 'next-intl';
import Divider from 'components/containers/divider';
import { TicketLink } from 'components/homepage/ticket-link';
import { IconBlock, type IconComponent } from 'components/homepage/icon-block';
import {
	getOperatingSchedule,
	formatTime,
	getLocalMidnight,
} from 'lib/visit/locationSchedule';
import TicketsIcon from 'assets/icons/tickets.svg';
import CalendarIcon from 'assets/icons/homepage-calendar.svg';
import ClockIcon from 'assets/icons/homepage-clock.svg';
import LocationPinIcon from 'assets/icons/homepage-location-pin.svg';
import styles from './homepage-info-section.module.scss';

export function HomepageInfoSection({
	heading,
	operatingSchedules,
	quickTips,
	homepageBuyTicketsCode,
}: HomepageInfoSectionProps) {
	const t = useTranslations();
	const locale = useLocale();

	const schedule = getOperatingSchedule({ schedules: operatingSchedules });
	const formattedHours = {
		openTime: formatTime({
			date: getLocalMidnight(),
			time: schedule.hours.openTime,
			locale,
			short: false,
		}),
		closeTime: formatTime({
			date: getLocalMidnight(),
			time: schedule.hours.closeTime,
			locale,
			short: false,
		}),
	};
	const quickTipsArray: QuickTipWithIcon[] = [
		...Object.entries(quickTips).map(
			([key, value]: [keyof QuickTips, QuickTip]) => ({
				...value,
				icon: getIconForTip(key),
			}),
		),
		{
			icon: getIconForTip('hours'),
			title: schedule.isClosedToday
				? t('museumClosed')
				: t('openHoursMedium', formattedHours),
			body: t('closedOnWednesdays'),
		},
	];

	return (
		<section className={styles.container}>
			<div className={styles.headingContainer}>
				<h1 className={styles.heading}>{heading}</h1>
				<div className={styles.buttons}>
					<TicketLink
						navigationBuyTicketsCode={homepageBuyTicketsCode}
						className={styles.button}
						data-analytics-id="homepage-ticket-link"
					/>
					<Link
						href="/visit/plan-your-visit"
						className={cn('button', 'secondary', styles.button)}
						data-analytics-id="homepage-plan-your-visit-link"
					>
						{t('homepage.planYourVisit')}
					</Link>
				</div>
			</div>
			<Divider margin="var(--spacing-s)" />
			<QuickTips quickTips={quickTipsArray} />
		</section>
	);
}

function QuickTips({ quickTips }: QuickTipsProps) {
	return (
		<div className={styles.quickTips}>
			{quickTips.map(({ icon, title, body }) => (
				<div key={title} className={styles.quickTipWrapper}>
					<IconBlock IconComponent={icon} title={title} body={body} />
				</div>
			))}
		</div>
	);
}

function getIconForTip(type: QuickTipsKeys): IconComponent | null {
	switch (type) {
		case 'hours':
			return ClockIcon;
		case 'admission':
			return TicketsIcon;
		case 'location':
			return LocationPinIcon;
		case 'misc':
			return CalendarIcon;
		default:
			return null;
	}
}

interface HomepageInfoSectionProps {
	heading: string;
	operatingSchedules: SanityOperatingSchedule[];
	quickTips: QuickTips;
	homepageBuyTicketsCode?: string;
}
interface QuickTipsProps {
	quickTips: QuickTipWithIcon[];
}
interface QuickTips {
	admission: QuickTip;
	location: QuickTip;
	misc: QuickTip;
}
type QuickTipsKeys = keyof QuickTips | 'hours';
type QuickTipWithIcon = QuickTip & {
	icon: IconComponent;
};
interface QuickTip {
	title: string;
	body: string;
}
